import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { cn } from "../lib/cn";
import { humanizeEventType } from "../lib/vocab";
import { BreadcrumbTrail } from "../molecules/BreadcrumbTrail";
import { ErrorState } from "../molecules/ErrorState";
import { Skeleton } from "../molecules/Skeleton";
import { EventCard } from "../organisms/EventCard";
import { ProofTreeViewer } from "../organisms/ProofTreeViewer";

export interface EventDetailEvent {
	hash: string;
	eventType: string;
	respaCode: string;
	respaName: string;
	timestamp: string;
	verified: boolean;
	anchored: boolean;
	merkleRoot?: string | null;
	chainId?: string | null;
	chainName?: string | null;
}

export interface EventDetailProof {
	proof: string[];
	leafIndex: number;
	txHash?: string | null;
	blockNumber?: number | null;
	blockTimestamp?: string | null;
	polygonscanUrl?: string | null;
}

export interface EventDetailPageProps {
	event: EventDetailEvent | null;
	proof: EventDetailProof | null;
	loading?: boolean;
	error?: Error | null;
	onBack: () => void;
	onCopyHash: () => void;
	onGenerateReport: () => void;
	reportPending?: boolean;
	className?: string;
}

function EventDetailPage({
	event,
	proof,
	loading = false,
	error = null,
	onBack,
	onCopyHash,
	onGenerateReport,
	reportPending = false,
	className,
}: EventDetailPageProps): ReactNode {
	const eventName = event ? humanizeEventType(event.eventType) : "Detail";
	const breadcrumbs = [
		{ label: "Events", href: "/events" },
		{ label: eventName },
	];

	return (
		<div className={cn("flex flex-col gap-6", className)}>
			<BreadcrumbTrail items={breadcrumbs} />

			{error ? (
				<ErrorState
					error={error}
					title="Failed to load event"
					onRetry={onBack}
				/>
			) : loading ? (
				<div className="flex flex-col gap-4">
					<Skeleton className="h-36 w-full" />
					<Skeleton className="h-64 w-full" />
				</div>
			) : event ? (
				<>
					<div className="flex items-start justify-between gap-4">
						<Heading size="xl">{eventName}</Heading>
						<div className="flex gap-2 print:hidden">
							<Button variant="outline" onClick={onBack}>
								Back
							</Button>
							<Button onClick={onGenerateReport} disabled={reportPending}>
								{reportPending ? "Preparing…" : "Download certificate (PDF)"}
							</Button>
						</div>
					</div>

					<EventCard
						eventType={event.eventType}
						respaCode={event.respaCode}
						respaName={event.respaName}
						hash={event.hash}
						timestamp={event.timestamp}
						verified={event.verified}
						anchored={event.anchored}
						onCopy={onCopyHash}
					/>

					{proof && event.merkleRoot && (
						<ProofTreeViewer
							leafHash={event.hash}
							proof={proof.proof}
							merkleRoot={event.merkleRoot}
							leafIndex={proof.leafIndex}
							txHash={proof.txHash}
							chainName={event.chainName}
							polygonscanUrl={proof.polygonscanUrl}
							verified={event.verified}
							anchored={event.anchored}
							recordedAt={event.timestamp}
						/>
					)}
				</>
			) : null}
		</div>
	);
}
EventDetailPage.displayName = "EventDetailPage";

export default EventDetailPage;
export { EventDetailPage };
